import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FinancialConfig } from './FinancialConfig.js';
import { SMSFExportEngine } from './SMSFExportEngine';
import { formatInputValue, getNumericInputProps } from './utils/inputHelpers.js';
import CalculationTransparency from './components/CalculationTransparency.js';

const MAX_LRBA_LVR_PCT = Math.round(FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TO_VALUE * 100);

const SMSFLRBACalculator = ({ smsfState, setSmsfState }) => {
    const [activeTab, setActiveTab] = useState('overview');
    
    // LRBA State
    const [lrbaData, setLrbaData] = useState({
        propertyValue: 500000,
        loanAmount: 400000,
        interestRate: 6.5,
        loanTerm: 25,
        rentalIncome: 24000,
        propertyExpenses: 8000,
        establishmentCosts: 15000,
        legalFees: 3000,
        valuationFees: 800,
        stampDuty: 0, // Calculated
        propertyType: 'residential', // residential, commercial, industrial
        repaymentType: 'principal_interest', // principal_interest, interest_only
        interestOnlyPeriod: 0
    });

    const [projectionYears, setProjectionYears] = useState(10);
    const [propertyGrowthRate, setPropertyGrowthRate] = useState(4.0);
    const [rentalGrowthRate, setRentalGrowthRate] = useState(2.5);
    const [expenseGrowthRate, setExpenseGrowthRate] = useState(2.5);

    // LRBA Compliance Rules
    const LRBA_RULES = {
        maxLVR: FinancialConfig.SMSF.BORROWING.LRBA.MAXIMUM_LOAN_TO_VALUE, // Maximum LVR from config (70%)
        minLVR: 0.50, // Practical minimum 50% LVR
        armslength: true, // Must be arms-length transaction
        singleAcquirableAsset: true, // Single acquirable asset rule
        noRecourse: true, // Limited recourse to SMSF only
        lenderTypes: ['bank', 'related_party', 'third_party'],
        prohibitedAssets: ['collectibles', 'personal_use_assets']
    };

    // Calculate stamp duty based on state (using NSW as default)
    const calculateStampDuty = (propertyValue, state = 'NSW') => {
        // NSW stamp duty rates (simplified)
        if (propertyValue <= 16000) return propertyValue * 0.0125;
        if (propertyValue <= 35000) return 200 + (propertyValue - 16000) * 0.015;
        if (propertyValue <= 93000) return 485 + (propertyValue - 35000) * 0.0175;
        if (propertyValue <= 351000) return 1500 + (propertyValue - 93000) * 0.035;
        if (propertyValue <= 1168000) return 10530 + (propertyValue - 351000) * 0.045;
        return 47295 + (propertyValue - 1168000) * 0.055;
    };

    // Calculate loan metrics
    const calculateLoanMetrics = () => {
        const { propertyValue, loanAmount, interestRate, loanTerm, rentalIncome, propertyExpenses, repaymentType, interestOnlyPeriod } = lrbaData;
        
        const monthlyRate = interestRate / 100 / 12;
        const totalPayments = loanTerm * 12;
        
        let monthlyPayment;
        if (repaymentType === 'interest_only') {
            monthlyPayment = loanAmount * monthlyRate;
        } else {
            monthlyPayment = loanAmount * (monthlyRate * Math.pow(1 + monthlyRate, totalPayments)) / (Math.pow(1 + monthlyRate, totalPayments) - 1);
        }
        
        const annualPayment = monthlyPayment * 12;
        const monthlyRentalIncome = rentalIncome / 12;
        const monthlyExpenses = propertyExpenses / 12;
        const monthlyCashFlow = monthlyRentalIncome - monthlyExpenses - monthlyPayment;
        const annualCashFlow = monthlyCashFlow * 12;
        
        const lvr = (loanAmount / propertyValue) * 100;
        const equity = propertyValue - loanAmount;
        const rentalYield = (rentalIncome / propertyValue) * 100;
        
        const stampDuty = calculateStampDuty(propertyValue);
        const totalAcquisitionCost = propertyValue + stampDuty + lrbaData.establishmentCosts + lrbaData.legalFees + lrbaData.valuationFees;
        const totalUpfrontCosts = equity + stampDuty + lrbaData.establishmentCosts + lrbaData.legalFees + lrbaData.valuationFees;
        
        // First year interest for tax deduction
        const firstYearInterest = loanAmount * (interestRate / 100);
        
        return {
            monthlyPayment,
            annualPayment,
            monthlyCashFlow,
            annualCashFlow,
            lvr,
            equity,
            rentalYield,
            stampDuty,
            totalAcquisitionCost,
            totalUpfrontCosts,
            firstYearInterest,
            isCompliant: lvr <= LRBA_RULES.maxLVR * 100,
            cashFlowPositive: annualCashFlow >= 0
        };
    };

    // Generate projections
    const generateProjections = () => {
        const metrics = calculateLoanMetrics();
        const projections = [];
        
        let currentPropertyValue = lrbaData.propertyValue;
        let currentLoanBalance = lrbaData.loanAmount;
        let currentRentalIncome = lrbaData.rentalIncome;
        let currentExpenses = lrbaData.propertyExpenses;
        
        const monthlyRate = lrbaData.interestRate / 100 / 12;
        const monthlyPayment = metrics.monthlyPayment;
        
        for (let year = 0; year <= projectionYears; year++) {
            if (year > 0) {
                currentPropertyValue *= (1 + propertyGrowthRate / 100);
                currentRentalIncome *= (1 + rentalGrowthRate / 100);
                currentExpenses *= (1 + expenseGrowthRate / 100);
                
                // Calculate loan balance after 12 monthly payments
                for (let month = 0; month < 12; month++) {
                    const interestPayment = currentLoanBalance * monthlyRate;
                    const principalPayment = lrbaData.repaymentType === 'interest_only' ? 0 : monthlyPayment - interestPayment;
                    currentLoanBalance -= principalPayment;
                    if (currentLoanBalance < 0) currentLoanBalance = 0;
                }
            }
            
            const equity = currentPropertyValue - currentLoanBalance;
            const lvr = (currentLoanBalance / currentPropertyValue) * 100;
            const annualInterest = currentLoanBalance * (lrbaData.interestRate / 100);
            const annualPrincipal = lrbaData.repaymentType === 'interest_only' ? 0 : (monthlyPayment * 12) - annualInterest;
            const netCashFlow = currentRentalIncome - currentExpenses - (monthlyPayment * 12);
            const cumulativeCashFlow = year === 0 ? netCashFlow : projections[year - 1].cumulativeCashFlow + netCashFlow;
            
            projections.push({
                year,
                propertyValue: currentPropertyValue,
                loanBalance: currentLoanBalance,
                equity,
                lvr,
                rentalIncome: currentRentalIncome,
                expenses: currentExpenses,
                loanRepayment: monthlyPayment * 12,
                interestPaid: annualInterest,
                principalPaid: annualPrincipal,
                netCashFlow,
                cumulativeCashFlow,
                capitalGain: year === 0 ? 0 : currentPropertyValue - lrbaData.propertyValue,
                totalReturn: year === 0 ? 0 : equity - (lrbaData.propertyValue - lrbaData.loanAmount)
            });
        }
        
        return projections;
    };

    // Compliance check
    const checkCompliance = () => {
        const metrics = calculateLoanMetrics();
        const issues = [];
        const warnings = [];
        
        if (metrics.lvr > LRBA_RULES.maxLVR * 100) {
            issues.push({
                severity: 'critical',
                message: `LVR of ${metrics.lvr.toFixed(1)}% exceeds maximum allowed ${LRBA_RULES.maxLVR * 100}%`,
                remedy: 'Reduce loan amount or increase deposit'
            });
        }
        
        if (metrics.lvr < LRBA_RULES.minLVR * 100) {
            warnings.push({
                severity: 'warning',
                message: `LVR of ${metrics.lvr.toFixed(1)}% is below typical minimum ${LRBA_RULES.minLVR * 100}%`,
                remedy: 'Consider if LRBA structure is necessary'
            });
        }
        
        if (!metrics.cashFlowPositive) {
            warnings.push({
                severity: 'warning',
                message: 'Negative cash flow - SMSF must have sufficient reserves',
                remedy: 'Ensure SMSF has adequate liquid assets to cover shortfall'
            });
        }
        
        if (lrbaData.propertyValue < 200000) {
            warnings.push({
                severity: 'info',
                message: 'Property value below typical LRBA minimum',
                remedy: 'Many lenders require minimum property value of $200,000'
            });
        }
        
        return {
            isCompliant: issues.length === 0,
            issues,
            warnings
        };
    };

    const metrics = calculateLoanMetrics();
    const projections = generateProjections();
    const compliance = checkCompliance();

    useEffect(() => {
        setLrbaData(prev => ({
            ...prev,
            stampDuty: calculateStampDuty(prev.propertyValue)
        }));
    }, [lrbaData.propertyValue]);

    const renderOverview = () => (
        <div className="space-y-6">
            {/* Compliance Status */}
            <div className={`p-4 rounded-lg border-2 ${
                compliance.isCompliant ? 'bg-green-50 border-green-500' : 'bg-red-50 border-red-500'
            }`}>
                <div className="flex items-center justify-between">
                    <div>
                        <h3 className="text-lg font-semibold">
                            {compliance.isCompliant ? '✅ LRBA Compliant' : '❌ Compliance Issues Detected'}
                        </h3>
                        <p className="text-sm text-gray-600">
                            {compliance.isCompliant 
                                ? 'This LRBA structure meets regulatory requirements'
                                : `${compliance.issues.length} critical issue(s) and ${compliance.warnings.length} warning(s)`
                            }
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-2xl font-bold">{metrics.lvr.toFixed(1)}%</div>
                        <div className="text-sm text-gray-600">Loan-to-Value Ratio</div>
                    </div>
                </div>
            </div>

            {/* Key Metrics */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">LRBA Key Metrics</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                        <div className="text-sm text-gray-600">Property Value</div>
                        <div className="text-2xl font-bold text-blue-600">${metrics.totalAcquisitionCost.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">Including costs</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                        <div className="text-sm text-gray-600">Loan Amount</div>
                        <div className="text-2xl font-bold text-green-600">${lrbaData.loanAmount.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">{metrics.lvr.toFixed(1)}% LVR</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                        <div className="text-sm text-gray-600">Required Equity</div>
                        <div className="text-2xl font-bold text-purple-600">${metrics.totalUpfrontCosts.toLocaleString()}</div>
                        <div className="text-xs text-gray-500">Deposit + costs</div>
                    </div>
                    <div className={`p-4 rounded-lg ${metrics.cashFlowPositive ? 'bg-green-50' : 'bg-red-50'}`}>
                        <div className="text-sm text-gray-600">Annual Cash Flow</div>
                        <div className={`text-2xl font-bold ${metrics.cashFlowPositive ? 'text-green-600' : 'text-red-600'}`}>
                            ${Math.abs(metrics.annualCashFlow).toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">{metrics.cashFlowPositive ? 'Positive' : 'Negative'}</div>
                    </div>
                </div>
            </div>

            {/* Loan Details */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Loan Structure</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Monthly Repayment:</span>
                            <span className="font-semibold">${metrics.monthlyPayment.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Annual Repayment:</span>
                            <span className="font-semibold">${metrics.annualPayment.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Interest Rate:</span>
                            <span className="font-semibold">{lrbaData.interestRate}%</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Loan Term:</span>
                            <span className="font-semibold">{lrbaData.loanTerm} years</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Repayment Type:</span>
                            <span className="font-semibold capitalize">{lrbaData.repaymentType.replace('_', ' & ')}</span>
                        </div>
                    </div>
                    <div className="space-y-3">
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Rental Income:</span>
                            <span className="font-semibold text-green-600">${lrbaData.rentalIncome.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Property Expenses:</span>
                            <span className="font-semibold text-red-600">-${lrbaData.propertyExpenses.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Rental Yield:</span>
                            <span className="font-semibold">{metrics.rentalYield.toFixed(2)}%</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">First Year Interest:</span>
                            <span className="font-semibold">${metrics.firstYearInterest.toLocaleString()}</span>
                        </div>
                        <div className="flex justify-between py-2 border-b border-gray-200">
                            <span className="text-gray-600">Tax Deductible:</span>
                            <span className="font-semibold text-green-600">Yes (15%)</span>
                        </div>
                    </div>
                </div>
            </div>

            {/* Acquisition Costs */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Acquisition Costs Breakdown</h3>
                <div className="space-y-3">
                    {[
                        { label: 'Property Purchase Price', value: lrbaData.propertyValue },
                        { label: 'Stamp Duty', value: metrics.stampDuty },
                        { label: 'Legal Fees', value: lrbaData.legalFees },
                        { label: 'Valuation Fees', value: lrbaData.valuationFees },
                        { label: 'Loan Establishment Costs', value: lrbaData.establishmentCosts },
                        { label: 'Total Acquisition Cost', value: metrics.totalAcquisitionCost, isTotal: true }
                    ].map((cost, index) => (
                        <div key={index} className={`flex justify-between py-2 ${
                            cost.isTotal ? 'border-t-2 border-gray-300 pt-3 mt-3' : 'border-b border-gray-200'
                        }`}>
                            <span className={`${cost.isTotal ? 'font-bold text-gray-800' : 'text-gray-600'}`}>
                                {cost.label}:
                            </span>
                            <span className={`${cost.isTotal ? 'font-bold text-blue-600 text-lg' : 'font-semibold'}`}>
                                ${cost.value.toLocaleString()}
                            </span>
                        </div>
                    ))}
                </div>
            </div>

            {/* Compliance Issues & Warnings */}
            {(compliance.issues.length > 0 || compliance.warnings.length > 0) && (
                <div className="space-y-4">
                    {compliance.issues.map((issue, index) => (
                        <div key={index} className="border-l-4 border-red-500 bg-red-50 p-4 rounded-r-lg">
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className="font-semibold text-red-800">Critical Issue</h4>
                                    <p className="text-sm text-red-700">{issue.message}</p>
                                    <p className="text-sm text-red-600 mt-2"><strong>Remedy:</strong> {issue.remedy}</p>
                                </div>
                            </div>
                        </div>
                    ))}
                    {compliance.warnings.map((warning, index) => (
                        <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                            warning.severity === 'warning' ? 'border-yellow-500 bg-yellow-50' : 'border-blue-500 bg-blue-50'
                        }`}>
                            <div className="flex justify-between items-start">
                                <div>
                                    <h4 className={`font-semibold ${
                                        warning.severity === 'warning' ? 'text-yellow-800' : 'text-blue-800'
                                    }`}>
                                        {warning.severity === 'warning' ? 'Warning' : 'Information'}
                                    </h4>
                                    <p className={`text-sm ${
                                        warning.severity === 'warning' ? 'text-yellow-700' : 'text-blue-700'
                                    }`}>
                                        {warning.message}
                                    </p>
                                    <p className={`text-sm mt-2 ${
                                        warning.severity === 'warning' ? 'text-yellow-600' : 'text-blue-600'
                                    }`}>
                                        <strong>Note:</strong> {warning.remedy}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}

            <CalculationTransparency
                kind="smsf"
                title="LRBA Transparency"
                inputs={{
                    propertyValue: lrbaData.propertyValue,
                    totalBalance: metrics.totalAcquisitionCost,
                    contributionAmount: lrbaData.loanAmount,
                }}
                results={{
                    ...metrics,
                    tableData: projections,
                    warnings: [...compliance.issues, ...compliance.warnings],
                }}
            />
        </div>
    );

    const renderCalculator = () => (
        <div className="space-y-6">
            {/* Property Details */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Property Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Property Value ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={lrbaData.propertyValue}
                            onChange={(e) => setLrbaData({ ...lrbaData, propertyValue: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Property Type
                        </label>
                        <select
                            value={lrbaData.propertyType}
                            onChange={(e) => setLrbaData({ ...lrbaData, propertyType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="residential">Residential</option>
                            <option value="commercial">Commercial</option>
                            <option value="industrial">Industrial</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Annual Rental Income ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={lrbaData.rentalIncome}
                            onChange={(e) => setLrbaData({ ...lrbaData, rentalIncome: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Annual Property Expenses ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={lrbaData.propertyExpenses}
                            onChange={(e) => setLrbaData({ ...lrbaData, propertyExpenses: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Loan Details */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Loan Details</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Loan Amount ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={lrbaData.loanAmount}
                            onChange={(e) => setLrbaData({ ...lrbaData, loanAmount: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                        <div className="text-sm text-gray-600 mt-1">
                            LVR: {metrics.lvr.toFixed(1)}% {metrics.lvr > MAX_LRBA_LVR_PCT && <span className="text-red-600">(Exceeds {MAX_LRBA_LVR_PCT}% limit)</span>}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Interest Rate (%)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            step="0.1"
                            value={lrbaData.interestRate}
                            onChange={(e) => setLrbaData({ ...lrbaData, interestRate: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Loan Term (years)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={lrbaData.loanTerm}
                            onChange={(e) => setLrbaData({ ...lrbaData, loanTerm: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Repayment Type
                        </label>
                        <select
                            value={lrbaData.repaymentType}
                            onChange={(e) => setLrbaData({ ...lrbaData, repaymentType: e.target.value })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        >
                            <option value="principal_interest">Principal & Interest</option>
                            <option value="interest_only">Interest Only</option>
                        </select>
                    </div>
                </div>
            </div>

            {/* Acquisition Costs */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Acquisition Costs</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Stamp Duty ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={metrics.stampDuty.toFixed(0)}
                            disabled
                            className="w-full px-3 py-2 border border-gray-300 rounded-md bg-gray-100"
                        />
                        <div className="text-sm text-gray-600 mt-1">Automatically calculated</div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Legal Fees ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={lrbaData.legalFees}
                            onChange={(e) => setLrbaData({ ...lrbaData, legalFees: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Valuation Fees ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={lrbaData.valuationFees}
                            onChange={(e) => setLrbaData({ ...lrbaData, valuationFees: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Loan Establishment Costs ($)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={lrbaData.establishmentCosts}
                            onChange={(e) => setLrbaData({ ...lrbaData, establishmentCosts: Number(e.target.value) })}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Calculate Button */}
            <div className="flex justify-center">
                <button
                    type="button"
                    onClick={() => setActiveTab('overview')}
                    className="px-6 py-3 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors font-semibold"
                >
                    Calculate LRBA Structure
                </button>
            </div>
        </div>
    );

    const renderProjections = () => (
        <div className="space-y-6">
            {/* Projection Settings */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Projection Settings</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Projection Years
                        </label>
                        <input
                            {...getNumericInputProps()}
                            value={projectionYears}
                            onChange={(e) => setProjectionYears(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Property Growth Rate (%)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            step="0.1"
                            value={propertyGrowthRate}
                            onChange={(e) => setPropertyGrowthRate(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Rental Growth Rate (%)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            step="0.1"
                            value={rentalGrowthRate}
                            onChange={(e) => setRentalGrowthRate(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Expense Growth Rate (%)
                        </label>
                        <input
                            {...getNumericInputProps()}
                            step="0.1"
                            value={expenseGrowthRate}
                            onChange={(e) => setExpenseGrowthRate(Number(e.target.value))}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        />
                    </div>
                </div>
            </div>

            {/* Summary Metrics */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Projection Summary ({projectionYears} Years)</h3>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-blue-600">
                            ${projections[projectionYears].propertyValue.toLocaleString()}
                        </div>
                        <div className="text-sm text-blue-700">Final Property Value</div>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-green-600">
                            ${projections[projectionYears].equity.toLocaleString()}
                        </div>
                        <div className="text-sm text-green-700">Final Equity</div>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                        <div className="text-2xl font-bold text-purple-600">
                            ${projections[projectionYears].capitalGain.toLocaleString()}
                        </div>
                        <div className="text-sm text-purple-700">Capital Gain</div>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${
                        projections[projectionYears].cumulativeCashFlow >= 0 ? 'bg-green-50' : 'bg-red-50'
                    }`}>
                        <div className={`text-2xl font-bold ${
                            projections[projectionYears].cumulativeCashFlow >= 0 ? 'text-green-600' : 'text-red-600'
                        }`}>
                            ${Math.abs(projections[projectionYears].cumulativeCashFlow).toLocaleString()}
                        </div>
                        <div className={`text-sm ${
                            projections[projectionYears].cumulativeCashFlow >= 0 ? 'text-green-700' : 'text-red-700'
                        }`}>
                            Cumulative Cash Flow
                        </div>
                    </div>
                </div>
            </div>

            {/* Detailed Projections Table */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Detailed Year-by-Year Projections</h3>
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead>
                            <tr className="border-b border-gray-200">
                                <th className="text-left py-3 px-4">Year</th>
                                <th className="text-right py-3 px-4">Property Value</th>
                                <th className="text-right py-3 px-4">Loan Balance</th>
                                <th className="text-right py-3 px-4">Equity</th>
                                <th className="text-right py-3 px-4">LVR</th>
                                <th className="text-right py-3 px-4">Rental Income</th>
                                <th className="text-right py-3 px-4">Net Cash Flow</th>
                                <th className="text-right py-3 px-4">Cumulative CF</th>
                            </tr>
                        </thead>
                        <tbody>
                            {projections.slice(0, Math.min(11, projections.length)).map((proj, index) => (
                                <tr key={index} className={`border-b border-gray-100 ${
                                    index === 0 ? 'bg-blue-50' : ''
                                }`}>
                                    <td className="py-3 px-4 font-medium">
                                        {index === 0 ? 'Current' : `Year ${index}`}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        ${proj.propertyValue.toLocaleString()}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        ${proj.loanBalance.toLocaleString()}
                                    </td>
                                    <td className="py-3 px-4 text-right font-medium text-green-600">
                                        ${proj.equity.toLocaleString()}
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        {proj.lvr.toFixed(1)}%
                                    </td>
                                    <td className="py-3 px-4 text-right">
                                        ${proj.rentalIncome.toLocaleString()}
                                    </td>
                                    <td className={`py-3 px-4 text-right font-medium ${
                                        proj.netCashFlow >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        ${proj.netCashFlow.toLocaleString()}
                                    </td>
                                    <td className={`py-3 px-4 text-right font-medium ${
                                        proj.cumulativeCashFlow >= 0 ? 'text-green-600' : 'text-red-600'
                                    }`}>
                                        ${proj.cumulativeCashFlow.toLocaleString()}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );

    const renderCompliance = () => (
        <div className="space-y-6">
            {/* LRBA Compliance Rules */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">LRBA Compliance Requirements</h3>
                <div className="space-y-4">
                    {[
                        {
                            rule: 'Limited Recourse',
                            description: 'Lender can only claim against the asset purchased, not other SMSF assets',
                            status: true,
                            details: 'Lender\'s recourse is limited to the asset held in the holding trust'
                        },
                        {
                            rule: 'Single Acquirable Asset',
                            description: 'Can only purchase a single acquirable asset (or collection of identical assets)',
                            status: true,
                            details: 'Each LRBA must relate to a single acquirable asset'
                        },
                        {
                            rule: 'Arms-Length Terms',
                            description: 'Loan must be on commercial arms-length terms',
                            status: true,
                            details: 'Interest rate and terms must be commercially reasonable'
                        },
                        {
                            rule: 'LVR Limit',
                            description: `Maximum ${MAX_LRBA_LVR_PCT}% loan-to-value ratio (regulatory and lender requirement)`,
                            status: metrics.lvr <= 80,
                            details: `Current LVR: ${metrics.lvr.toFixed(1)}%`
                        },
                        {
                            rule: 'Holding Trust',
                            description: 'Asset must be held in a separate holding trust',
                            status: true,
                            details: 'Bare trust structure required with SMSF trustee as beneficiary'
                        },
                        {
                            rule: 'No Improvements',
                            description: 'Cannot make improvements that change the character of the asset before fully paid',
                            status: true,
                            details: 'Repairs and maintenance allowed, but not renovations or improvements'
                        },
                        {
                            rule: 'Related Party Loans',
                            description: 'Can borrow from related parties, but must be on commercial terms',
                            status: true,
                            details: 'Written loan agreement required with market interest rate'
                        }
                    ].map((item, index) => (
                        <div key={index} className={`border-l-4 p-4 rounded-r-lg ${
                            item.status ? 'border-green-500 bg-green-50' : 'border-red-500 bg-red-50'
                        }`}>
                            <div className="flex items-start">
                                <div className="flex-shrink-0 mr-3 mt-1">
                                    {item.status ? (
                                        <span className="text-green-600 text-xl">✓</span>
                                    ) : (
                                        <span className="text-red-600 text-xl">✗</span>
                                    )}
                                </div>
                                <div className="flex-1">
                                    <h4 className={`font-semibold ${item.status ? 'text-green-800' : 'text-red-800'}`}>
                                        {item.rule}
                                    </h4>
                                    <p className={`text-sm ${item.status ? 'text-green-700' : 'text-red-700'}`}>
                                        {item.description}
                                    </p>
                                    <p className={`text-sm mt-1 ${item.status ? 'text-green-600' : 'text-red-600'}`}>
                                        {item.details}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {/* Tax Implications */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <h3 className="text-lg font-semibold mb-4">Tax Implications</h3>
                <div className="space-y-4">
                    <div className="p-4 bg-blue-50 rounded-lg">
                        <h4 className="font-semibold text-blue-800 mb-2">Income Tax (Accumulation Phase)</h4>
                        <ul className="text-sm text-blue-700 space-y-1">
                            <li>• Rental income taxed at 15%</li>
                            <li>• Interest and expenses are tax deductible</li>
                            <li>• Net rental income: ${(lrbaData.rentalIncome - lrbaData.propertyExpenses - metrics.firstYearInterest).toLocaleString()}</li>
                            <li>• Tax payable: ${((lrbaData.rentalIncome - lrbaData.propertyExpenses - metrics.firstYearInterest) * 0.15).toLocaleString()}</li>
                        </ul>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg">
                        <h4 className="font-semibold text-green-800 mb-2">Capital Gains Tax (CGT)</h4>
                        <ul className="text-sm text-green-700 space-y-1">
                            <li>• CGT at 10% if held for 12+ months (15% with 1/3 discount)</li>
                            <li>• CGT at 0% if sold in pension phase</li>
                            <li>• Projected capital gain: ${projections[projectionYears].capitalGain.toLocaleString()}</li>
                            <li>• Estimated CGT (accumulation): ${(projections[projectionYears].capitalGain * 0.10).toLocaleString()}</li>
                        </ul>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg">
                        <h4 className="font-semibold text-purple-800 mb-2">Pension Phase Benefits</h4>
                        <ul className="text-sm text-purple-700 space-y-1">
                            <li>• 0% tax on rental income in pension phase</li>
                            <li>• 0% CGT on property sale in pension phase</li>
                            <li>• Interest still deductible but no tax benefit if income is zero</li>
                            <li>• Significant tax savings once transferred to pension phase</li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <div className="space-y-6 p-6 bg-gray-50 rounded-lg shadow-inner">
            <div className="flex items-center justify-between">
                <h2 className="text-2xl font-bold text-gray-800">LRBA Calculator</h2>
                <div className="flex space-x-2">
                    <button
                        onClick={() => setActiveTab('overview')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'overview' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Overview
                    </button>
                    <button
                        onClick={() => setActiveTab('calculator')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'calculator' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Calculator
                    </button>
                    <button
                        onClick={() => setActiveTab('projections')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'projections' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Projections
                    </button>
                    <button
                        onClick={() => setActiveTab('compliance')}
                        className={`px-4 py-2 rounded-md transition-colors ${
                            activeTab === 'compliance' 
                                ? 'bg-blue-600 text-white' 
                                : 'bg-white text-gray-700 hover:bg-gray-50'
                        }`}
                    >
                        Compliance
                    </button>
                </div>
            </div>

            {/* Content based on active tab */}
            {activeTab === 'overview' && renderOverview()}
            {activeTab === 'calculator' && renderCalculator()}
            {activeTab === 'projections' && renderProjections()}
            {activeTab === 'compliance' && renderCompliance()}

            {/* Action Buttons */}
            <div className="bg-white p-6 rounded-lg shadow-md">
                <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-600">
                        LRBA Analysis as of: {new Date().toLocaleDateString()}
                    </div>
                    <div className="flex space-x-4">
                        <button 
                            onClick={() => SMSFExportEngine.exportToPDF('lrba', {
                                ...lrbaData,
                                fundName: smsfState.smsfData?.fundName
                            })}
                            className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 transition-colors"
                        >
                            Export LRBA Report (PDF)
                        </button>
                        <button className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
                            Save Configuration
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

SMSFLRBACalculator.propTypes = {
    smsfState: PropTypes.object.isRequired,
    setSmsfState: PropTypes.func.isRequired
};

export default SMSFLRBACalculator;
